import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crosshair, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ModelPicker } from "@/components/common/ModelPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const formatMatchDate = (value) => {
    if (!value) return "Date unavailable";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? "Date unavailable" : parsed.toLocaleString();
};

export default function Matches() {
    const [matches, setMatches] = useState(null);
    const [active, setActive] = useState(null);
    const [report, setReport] = useState(null);
    const [model, setModel] = useState("gpt-5.2");
    const [analyzing, setAnalyzing] = useState(false);

    const load = async () => {
        const { data } = await api.get("/matches");
        setMatches(data.matches);
    };
    useEffect(() => { load(); }, []);

    const openMatch = async (m) => {
        setActive(m); setReport(null);
        const { data } = await api.get(`/matches/${m.id}`);
        if (data.report) setReport(data.report);
    };

    const analyze = async () => {
        setAnalyzing(true);
        try {
            const { data } = await api.post(`/matches/${active.id}/analyze`, { model });
            setReport(data);
            if (data.generation_status === "fallback") toast.warning(data.generation_note || "AI was unavailable; deterministic guidance was used.");
            else toast.success("Match analyzed by AI coach.");
            load();
        } catch (err) {
            toast.error(apiError(err.response?.data?.detail) || "Analysis failed.");
        } finally { setAnalyzing(false); }
    };

    if (!matches) return <Loader label="Loading matches" />;

    return (
        <div className="ww-fade-up">
            <PageHeader title="Matches" subtitle="Review every match and get an instant AI breakdown." />

            <div className="bg-surface border border-border rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="text-left font-semibold px-4 py-3">Game / Map</th>
                                <th className="text-left font-semibold px-4 py-3">Result</th>
                                <th className="text-right font-semibold px-4 py-3 font-mono">K / D / A</th>
                                <th className="text-right font-semibold px-4 py-3 font-mono">K/D</th>
                                <th className="text-right font-semibold px-4 py-3 font-mono hidden sm:table-cell">HS%</th>
                                <th className="text-right font-semibold px-4 py-3">AI</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matches.map((m) => (
                                <tr key={m.id} onClick={() => openMatch(m)} data-testid={`match-row-${m.id}`}
                                    className="border-b border-border/50 hover:bg-white/[0.03] cursor-pointer transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="text-white capitalize">{m.game}</div>
                                        <div className="text-xs text-muted-foreground">{m.map} · {m.mode}</div>
                                        <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                                            {formatMatchDate(m.date)}
                                            {m.capture_session_id ? ` · Capture ${m.capture_session_id.slice(0, 8)}` : ""}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3"><StatusBadge status={m.result === "Victory" ? "Victory" : m.result === "Defeat" ? "Defeat" : m.result?.startsWith("Top") ? "Normal" : "Unverified"} /></td>
                                    <td className="px-4 py-3 text-right font-mono text-white">{m.kills} / {m.deaths} / {m.assists}</td>
                                    <td className={cn("px-4 py-3 text-right font-mono", m.kd >= 1 ? "text-success" : "text-alert")}>{m.kd}</td>
                                    <td className="px-4 py-3 text-right font-mono text-muted-foreground hidden sm:table-cell">{m.headshot_pct}%</td>
                                    <td className="px-4 py-3 text-right">
                                        {m.analyzed ? <CheckCircle2 className="w-4 h-4 text-success inline" /> : <span className="text-xs text-muted-foreground">—</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent className="bg-surface border-border max-w-2xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            <Crosshair className="w-5 h-5 text-volt" /> {active?.map} <span className="text-muted-foreground text-sm capitalize">· {active?.game}</span>
                        </DialogTitle>
                    </DialogHeader>

                    {active && (
                        <>
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                                {[["Result", active.result], ["Kills", active.kills], ["Deaths", active.deaths], ["K/D", active.kd], ["Damage", active.damage]].map(([k, v]) => (
                                    <div key={k} className="bg-obsidian border border-border rounded-sm p-2.5 text-center">
                                        <div className="text-[10px] uppercase text-muted-foreground tracking-wider">{k}</div>
                                        <div className="font-mono text-white text-sm mt-0.5">{v}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="text-[11px] text-muted-foreground font-mono">
                                {formatMatchDate(active.date)}
                                {active.capture_session_id ? ` · Capture ${active.capture_session_id}` : ""}
                                {active.stat_source === "personal_scoreboard"
                                    ? ` · Verified scoreboard (${active.scoreboard_confirmations || 2} reads)`
                                    : " · Legacy/unverified source"}
                            </div>

                            <div className="flex items-center justify-between gap-3 mt-2">
                                <ModelPicker value={model} onChange={setModel} />
                                <button data-testid="analyze-match" onClick={analyze} disabled={analyzing}
                                    className="h-9 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2 disabled:opacity-60">
                                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    {report ? "Re-analyze" : "Analyze with AI"}
                                </button>
                            </div>

                            {analyzing && <div className="text-xs text-muted-foreground font-mono animate-pulse">AI coach is reviewing the match...</div>}

                            {report && (
                                <div className="mt-2 space-y-4" data-testid="match-report">
                                    <div className="bg-obsidian border border-border rounded-sm p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Coach Summary</span>
                                            <span className="font-mono text-volt">Rating {report.rating}</span>
                                        </div>
                                        <p className="text-sm text-foreground/90">{report.summary}</p>
                                    </div>
                                    {report.sections?.map((s, i) => (
                                        <div key={i} className="border-l-2 border-volt/50 pl-4">
                                            <div className="font-heading font-semibold text-white text-sm">{s.title}</div>
                                            <p className="text-sm text-muted-foreground mt-1">{s.content}</p>
                                        </div>
                                    ))}
                                    <div className="text-[11px] text-muted-foreground font-mono">
                                        {report.generation_status === "fallback" ? "Deterministic fallback" : `Generated by ${report.ai_model}`}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
